import {
  Box, Button, Checkbox, DialogContent,
  FormControlLabel, Grid, MenuItem, TextField, Typography, Divider, Alert, Radio, RadioGroup, FormLabel, Autocomplete
} from '@mui/material';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { IOptions } from '../FormFields/CustomSelect';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiStatus } from "../../utils/api.types";
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { calculateDailyWage, processDailyWage } from '../../store/salaryStore/salaryThunk';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchCostCentres } from '../../store/costCentreStore/costCentreThunk';

interface DialogProcessDailyWageFormProps {
  title: String;
  open: boolean;
  onClose: (refresh: any) => void;
}

interface ITdsOption extends IOptions {
  rate?: number;
  taxSection?: string;
  taxName?: string;
}

const DialogProcessDailyWageForm: FC<DialogProcessDailyWageFormProps> = ({ onClose, open, title }) => {

  const { status, salaryCalculation } = useAppSelector(state => state.salaries);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { employees } = useAppSelector(state => state.employees);
  const { costCentres } = useAppSelector(state => state.costCentres);
  const dispatch = useAppDispatch();

  // Form states
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  // Wage calculation states
  const [calculationMethod, setCalculationMethod] = useState<'PER_DAY' | 'PER_HOUR'>('PER_DAY');
  const [ratePerDay, setRatePerDay] = useState(0);
  const [daysWorked, setDaysWorked] = useState(0);
  const [ratePerHour, setRatePerHour] = useState(0);
  const [hoursWorked, setHoursWorked] = useState(0);
  const [overtimeHours, setOvertimeHours] = useState(0);
  const [overtimeRate, setOvertimeRate] = useState(0);

  // Bank account states
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);

  // Cost centre state
  const [selectedCostCentre, setSelectedCostCentre] = useState('');

  // Payment mode states
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [paymentStatus, setPaymentStatus] = useState('PROCESSED');

  // Tax configuration states
  const [applyTax, setApplyTax] = useState(false);

  // TDS states
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<ITdsOption[]>([]);
  const [tdsSelected, setTdsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  });

  // Form validation schema
  const schema = yup.object().shape({
    employeeId: yup.string().required("Employee is required."),
  });

  const methods = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema)
  });

  // Load TDS rates once when form opens
  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const getTdsRates = async () => {
      try {
        const taxRatesData: any = await dispatch(fetchTaxRates()).unwrap();
        if (taxRatesData && taxRatesData.length > 0 && isMounted) {
          const tdsRates = taxRatesData.filter((item: any) => item.taxType === "tds");
          const tdsOptions = tdsRates.map((item: any) => ({
            label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
            value: item._id,
            rate: item.taxRate,
            taxSection: item.taxSection,
            taxName: item.taxName
          }));
          setTdsTaxRateOptions(tdsOptions);
        }
      } catch (error) {
        console.error('Error fetching TDS rates:', error);
      }
    };

    getTdsRates();

    return () => {
      isMounted = false;
    };
  }, [dispatch, open]);

  // Load employees, banks, and cost centres when form opens
  useEffect(() => {
    if (!open || !selectedAccount?._id) return;

    let isMounted = true;

    const loadData = async () => {
      try {
        // Load employees
        await dispatch(fetchEmployees({
          accountId: selectedAccount._id,
          type: 'list',
          params: ''
        }));

        // Load bank accounts
        const bankData: any = await dispatch(fetchBanks(selectedAccount._id));
        if (bankData.payload?.banks?.length > 0 && isMounted) {
          const bankOptions = bankData.payload.banks.map((c: any) => ({
            label: c.accountName + '-' + c.bankName,
            value: c._id
          }));
          setFromBankAccountOptions(bankOptions);

          const firstBank = bankData.payload.banks[0];
          methods.setValue('fromBankAccount', firstBank._id);
          setSelectedFromBankAccount(firstBank._id);
        }

        // Load cost centres (only those enabled for payroll)
        await dispatch(fetchCostCentres({ activeOnly: true, useForPayroll: true }));
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [selectedAccount?._id, open, dispatch]);

  // Calculate days worked when dates change
  useEffect(() => {
    if (fromDate && toDate && calculationMethod === 'PER_DAY') {
      const from = new Date(fromDate);
      const to = new Date(toDate);

      // Calculate the difference in days (inclusive)
      const timeDiff = to.getTime() - from.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both dates

      // Ensure we don't set negative days
      setDaysWorked(daysDiff >= 0 ? daysDiff : 0);
    }
  }, [fromDate, toDate, calculationMethod]);

  // Watch for employee selection changes and auto-populate cost centre
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'employeeId' && value.employeeId) {
        const selectedEmployee = employees.find((emp: any) => emp._id === value.employeeId);
        if (selectedEmployee) {
          const empData: any = selectedEmployee.employee || selectedEmployee;
          // Auto-populate cost centre from employee's default
          if (empData.costCentre) {
            setSelectedCostCentre(empData.costCentre);
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, employees]);

  // Auto-change payment status to PAID when CASH is selected
  useEffect(() => {
    if (paymentMode === 'CASH' && paymentStatus === 'PROCESSED') {
      setPaymentStatus('PAID');
    }
  }, [paymentMode, paymentStatus]);

  // Memoize TDS options
  const tdsOptionsWithDefault = useMemo(() => {
    return [{ label: 'Default (10% if > ₹15K)', value: '', rate: 0 }, ...tdsTaxRateOptions];
  }, [tdsTaxRateOptions]);

  // Handle TDS selection change
  const handleTdsChange = (event: any) => {
    const selectedTdsId = event.target.value;

    if (!selectedTdsId) {
      setTdsSelected({ _id: "", label: "Default TDS", rate: 0 });
      return;
    }

    const selectedTdsOption = tdsTaxRateOptions.find((opt: ITdsOption) => opt.value === selectedTdsId);
    if (selectedTdsOption) {
      setTdsSelected({
        _id: selectedTdsOption.value,
        label: selectedTdsOption.label,
        rate: selectedTdsOption.rate || 0
      });
    }
  };

  // Handle calculate button
  const handleCalculate = async () => {
    const employeeId = methods.getValues('employeeId');
    if (!employeeId) {
      alert('Please select an employee');
      return;
    }

    const wageData = {
      calculationMethod,
      ratePerDay,
      daysWorked,
      ratePerHour,
      hoursWorked,
      overtimeHours,
      overtimeRate,
      applyTax,
      tdsConfig: {
        apply: applyTax,
        rate: tdsSelected.rate,
        label: tdsSelected.label
      }
    };

    await dispatch(calculateDailyWage({
      employeeId,
      wageData
    }));
  };

  // Handle form submit
  const onSubmit = async (data: any) => {
    try {
      setFormSubmitted(true);

      const wageData = {
        calculationMethod,
        ratePerDay,
        daysWorked,
        ratePerHour,
        hoursWorked,
        overtimeHours,
        overtimeRate,
        applyTax,
        tdsConfig: {
          apply: applyTax,
          rate: tdsSelected.rate,
          label: tdsSelected.label
        }
      };

      const paymentOptions = {
        paymentStatus: paymentStatus as 'PROCESSED' | 'PAID',
        paymentMode,
        fromBankAccount: selectedFromBankAccount,
        remarks: data.remarks
      };

      await dispatch(processDailyWage({
        employeeId: data.employeeId,
        fromDate: new Date(fromDate),
        toDate: new Date(toDate),
        costCentre: selectedCostCentre || undefined,
        wageData,
        paymentOptions
      })).unwrap();

      methods.reset();
      setFormSubmitted(false);
      onClose(true);
    } catch (error: any) {
      setFormSubmitted(false);
      console.error('Error processing daily wage:', error);
    }
  };

  const handleClose = () => {
    methods.reset();
    setFormSubmitted(false);
    onClose(false);
  };

  const wageCalculation = salaryCalculation as any;

  const empOptions = (employees || []).map((emp: any) => ({
    label: `${emp.name || 'Unnamed'} (${emp.empId || '—'})`,
    value: emp._id,
    emp
  }));

  return (
    <div className="drawer-form-container" style={{ width: "100%", maxWidth: "100%", height: "100%", background: "#f9fafb" }}>
      <FormProvider {...methods}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", padding: 0, position: "relative" }} component="form" noValidate>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, height: 50, flexShrink: 0, bgcolor: 'transparent' }}>
            <h3 style={{ margin: 0 }}>Process Daily Wage Payment</h3>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' onClick={handleClose} size="small" sx={{ minWidth: '90px', height: '34px', textTransform: 'none' }}>Cancel</Button>
              <LoadingButton type="button" onClick={methods.handleSubmit(onSubmit)} variant="contained" loading={formSubmitted || status === ApiStatus.LOADING} size="small" sx={{ minWidth: '160px', height: '34px', textTransform: 'none' }}>
                Process Daily Wage
              </LoadingButton>
            </Box>
          </Box>

          {/* Body */}
          <DialogContent sx={{
            px: 1.5, py: 1, paddingBottom: '16px', overflowY: 'auto', flex: 1, bgcolor: '#f9fafb', fontFamily: 'Ubuntu',
            '& .c-text-field': {
              backgroundColor: '#fff !important', border: '1px solid #9da7b7 !important', borderRadius: '10px !important', padding: '0 !important', transition: 'border-color 0.15s ease',
              '&:hover': { borderColor: '#94a3b8 !important' },
              '&:focus-within': { borderColor: '#64748b !important', borderWidth: '1.5px !important' },
            },
            '& .c-text-field .MuiInputBase-root': { height: 36, minHeight: 36, fontSize: 14, padding: '0 10px !important', display: 'flex', alignItems: 'center', backgroundColor: 'transparent !important' },
            '& .c-text-field .MuiInputBase-input': { padding: '0 !important', height: 36, lineHeight: '36px', fontSize: 14, color: '#0f172a' },
            '& .c-text-field.MuiAutocomplete-root .MuiInputBase-root': { padding: '0 10px !important', height: 36, minHeight: 36 },
            '& .c-text-field.MuiAutocomplete-root .MuiAutocomplete-input': { padding: '0 !important', height: '34px !important', lineHeight: '34px !important' },
            // label look comes from the global .c-label (same as the Salary / Account forms); only spacing here
            '& .c-label': { margin: '0 0 5px 2px' }
          }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: '2px solid #dbe3ea', p: 1.5 }}>
              <Grid container spacing={1.5}>

                {/* Employee */}
                <Grid item xs={12} md={5}>
                  <h5 className='c-label'>Employee *</h5>
                  <Controller
                    name="employeeId"
                    control={methods.control}
                    render={({ field }) => {
                      const selected = empOptions.find(o => o.value === field.value) || null;
                      return (
                        <Autocomplete
                          size="small"
                          options={empOptions}
                          value={selected}
                          onChange={(_, opt) => field.onChange(opt?.value || '')}
                          getOptionLabel={(opt) => opt?.label || ''}
                          isOptionEqualToValue={(opt, val) => opt.value === val.value}
                          filterOptions={(opts, state) => {
                            const q = state.inputValue.trim().toLowerCase();
                            if (!q) return opts;
                            return opts.filter(o => String(o.emp?.name || '').toLowerCase().includes(q) || String(o.emp?.empId || '').toLowerCase().includes(q));
                          }}
                          renderOption={(props, opt) => (
                            <Box component="li" {...props} key={opt.value} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.8 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{opt.emp?.name || 'Unnamed'}</Typography>
                              <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{opt.emp?.empId || '—'}{opt.emp?.designation ? ` · ${opt.emp.designation}` : ''}</Typography>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} className='c-text-field' variant="standard" placeholder={`Search employee (${empOptions.length})`} size="small"
                              InputProps={{ ...params.InputProps, disableUnderline: true }}
                              error={Boolean(methods.formState.errors.employeeId)}
                              helperText={methods.formState.errors.employeeId?.message as string}
                            />
                          )}
                        />
                      );
                    }}
                  />
                </Grid>

                {/* Cost Centre */}
                {costCentres && costCentres.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <h5 className='c-label'>Cost Centre</h5>
                    <TextField select fullWidth className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={selectedCostCentre} onChange={(e) => setSelectedCostCentre(e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {costCentres.map((cc: any) => <MenuItem key={cc._id} value={cc._id}>{cc.alias ? `${cc.name} (${cc.alias})` : cc.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}

                {/* Calculation Method */}
                <Grid item xs={12} md={3}>
                  <h5 className='c-label'>Calculation Method</h5>
                  <RadioGroup row value={calculationMethod} onChange={(e) => setCalculationMethod(e.target.value as 'PER_DAY' | 'PER_HOUR')} sx={{ mt: 0.5 }}>
                    <FormControlLabel value="PER_DAY" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Per Day</Typography>} />
                    <FormControlLabel value="PER_HOUR" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Per Hour</Typography>} />
                  </RadioGroup>
                </Grid>

                {/* Dates */}
                <Grid item xs={6} md={3}>
                  <h5 className='c-label'>From Date</h5>
                  <TextField fullWidth type="date" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <h5 className='c-label'>To Date</h5>
                  <TextField fullWidth type="date" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </Grid>

                {/* Per Day Fields */}
                {calculationMethod === 'PER_DAY' && (<>
                  <Grid item xs={6} md={3}>
                    <h5 className='c-label'>Rate Per Day (₹)</h5>
                    <TextField fullWidth type="number" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={ratePerDay || ''} onChange={(e) => setRatePerDay(parseFloat(e.target.value))} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <h5 className='c-label'>Days Worked</h5>
                    <TextField fullWidth type="number" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={daysWorked || ''} onChange={(e) => setDaysWorked(parseFloat(e.target.value))} />
                  </Grid>
                </>)}

                {/* Per Hour Fields */}
                {calculationMethod === 'PER_HOUR' && (<>
                  <Grid item xs={6} md={3}>
                    <h5 className='c-label'>Rate Per Hour (₹)</h5>
                    <TextField fullWidth type="number" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={ratePerHour || ''} onChange={(e) => setRatePerHour(parseFloat(e.target.value))} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <h5 className='c-label'>Hours Worked</h5>
                    <TextField fullWidth type="number" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={hoursWorked || ''} onChange={(e) => setHoursWorked(parseFloat(e.target.value))} />
                  </Grid>
                </>)}

                {/* Overtime */}
                <Grid item xs={6} md={3}>
                  <h5 className='c-label'>Overtime Hours</h5>
                  <TextField fullWidth type="number" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={overtimeHours || ''} onChange={(e) => setOvertimeHours(parseFloat(e.target.value))} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <h5 className='c-label'>Overtime Rate (₹/hr)</h5>
                  <TextField fullWidth type="number" className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={overtimeRate || ''} onChange={(e) => setOvertimeRate(parseFloat(e.target.value))} />
                </Grid>

                {/* Payment */}
                <Grid item xs={6} md={3}>
                  <h5 className='c-label'>Payment Status</h5>
                  <TextField select fullWidth className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                    <MenuItem value="PROCESSED" disabled={paymentMode === 'CASH'}>Processed</MenuItem>
                    <MenuItem value="PAID">Paid</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} md={3}>
                  <h5 className='c-label'>Payment Mode</h5>
                  <TextField select fullWidth className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="NEFT">NEFT</MenuItem>
                    <MenuItem value="RTGS">RTGS</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="CHEQUE">Cheque</MenuItem>
                  </TextField>
                </Grid>

                {/* Bank Account */}
                {paymentMode !== 'CASH' && (
                  <Grid item xs={12} md={6}>
                    <h5 className='c-label'>From Bank Account</h5>
                    <TextField select fullWidth className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={selectedFromBankAccount} onChange={(e) => setSelectedFromBankAccount(e.target.value)}>
                      {fromBankAccountOptions.map((b: any) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}

                {/* TDS */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <FormControlLabel control={<Checkbox checked={applyTax} onChange={(e) => setApplyTax(e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: 13 }}>Apply TDS</Typography>} sx={{ m: 0 }} />
                  </Box>
                  {applyTax && (
                    <TextField select fullWidth className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" value={tdsSelected._id} onChange={handleTdsChange}>
                      {tdsOptionsWithDefault.map((t: any) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </TextField>
                  )}
                </Grid>

                {/* Remarks */}
                <Grid item xs={12}>
                  <h5 className='c-label'>Remarks</h5>
                  <Controller name="remarks" control={methods.control} render={({ field }) => (
                    <TextField {...field} fullWidth multiline rows={1} className='c-text-field' variant="standard" InputProps={{ disableUnderline: true }} size="small" placeholder="Optional remarks..." />
                  )} />
                </Grid>

                {/* Wage Preview */}
                {wageCalculation && wageCalculation.summary && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1, fontSize: 12 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Wage Breakdown</Typography>
                      <Typography sx={{ fontSize: 12 }}>Gross: ₹{wageCalculation.summary.grossWage?.toLocaleString('en-IN') || 0}</Typography>
                      <Typography sx={{ fontSize: 12 }}>TDS: ₹{wageCalculation.deductions?.tds?.toLocaleString('en-IN') || 0}</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Net Payable: ₹{wageCalculation.summary.netWage?.toLocaleString('en-IN') || 0}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
        </Box>
      </FormProvider>
    </div>
  );
};

export default DialogProcessDailyWageForm;
